import { useOutletContext } from "react-router-dom";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import "./PartnerPortal.css";

/**
 * Phase 1A: static security posture snapshot. Phase 1B expands this with
 * live per-partner audit trail, delivery health, and rate-limit charts.
 */
const CONTROLS = [
  { key: "mfa", label: "Multi-Factor Authentication (planned)",
    status: "planned",
    note: "Optional TOTP on partner accounts is planned for an upcoming release." },
  { key: "bruteforce", label: "Brute-force lockout on portal login",
    status: "implemented",
    note: "5 failed attempts → 15 minute lockout per IP + email." },
  { key: "session", label: "Signed JWT sessions (30-min TTL)",
    status: "implemented",
    note: "Partner sessions are cryptographically isolated from staff and volunteer sessions." },
  { key: "hmac", label: "HMAC-SHA256 signed webhooks",
    status: "implemented",
    note: "Every outbound delivery signs the payload + timestamp for replay protection." },
  { key: "oauth", label: "OAuth 2.0 Client Credentials (server-to-server)",
    status: "implemented",
    note: "Mint keys with the oauth:client scope, then exchange at /api/oauth/token for a 30-min JWT." },
  { key: "tls", label: "TLS 1.2+ on every endpoint",
    status: "implemented",
    note: "All traffic terminated at Cloudflare edge with modern ciphers." },
  { key: "audit", label: "Immutable audit trail",
    status: "implemented",
    note: "Every key/webhook/user change is written to audit_logs and surfaced on your Overview." },
  { key: "rotation", label: "One-click credential rotation",
    status: "implemented",
    note: "Revoke a key from the API Keys page — any client using it starts receiving 401s immediately." },
];

export default function PartnerSecurity() {
  const { me } = useOutletContext();
  return (
    <div data-testid="pp-security">
      <h1 className="hw-pp__page-title">
        <ShieldCheck className="w-5 h-5"
                      style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.4rem" }} />
        Security posture
      </h1>
      <p className="hw-pp__page-sub">
        HarborWell Connect&apos;s partner APIs are built to SOC 2 principles.
        This page shows what&apos;s already implemented for <strong>{me.partner_org.name}</strong>.
        Per-partner audit and delivery health views are on the way.
      </p>

      <div className="hw-pp__panel">
        <h3>Implemented controls</h3>
        <div>
          {CONTROLS.map((c) => (
            <div key={c.key} data-testid={`pp-sec-${c.key}`}
                  style={{ display: "flex", gap: "0.6rem", padding: "0.55rem 0",
                            borderBottom: "1px solid rgba(94,234,212,0.06)" }}>
              <CheckCircle2 className="w-4 h-4"
                             style={{ color: c.status === "implemented" ? "#22C55E" : "#F59E0B",
                                       flexShrink: 0, marginTop: "0.15rem" }} />
              <div>
                <div style={{ color: "#F1F5F9", fontSize: "0.9rem", fontWeight: 600 }}>
                  {c.label}
                  {c.status === "planned" && (
                    <span className="hw-pp__pill hw-pp__pill--invited"
                          style={{ marginLeft: "0.4rem" }}>Planned</span>
                  )}
                </div>
                <div style={{ color: "#94A3B8", fontSize: "0.78rem", marginTop: "0.15rem" }}>
                  {c.note}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="hw-pp__panel">
        <h3>Documentation & trust</h3>
        <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "#CBD5E1", fontSize: "0.85rem", lineHeight: 1.7 }}>
          <li><a href="/trust-center" target="_blank" rel="noreferrer"
                  style={{ color: "#5EEAD4" }}>Public Trust Center</a> — 14 implemented controls, live uptime, incident history.</li>
          <li><a href="/developer-portal" target="_blank" rel="noreferrer"
                  style={{ color: "#5EEAD4" }}>Developer Portal</a> — auth, webhook signing, event catalog, code samples.</li>
          <li><a href="/docs" target="_blank" rel="noreferrer"
                  style={{ color: "#5EEAD4" }}>OpenAPI reference</a> — machine-readable API contract for SDK generation.</li>
        </ul>
      </div>
    </div>
  );
}
